/**
 * WarehousePriceWorklist — mobile bulk price-update for warehouse items.
 *
 * The operator walks the warehouse on promo day and updates many prices
 * fast, instead of opening each post one by one. This view lists every
 * manually-added warehouse post (source = "warehouse:<channel>"), grouped
 * by store, each row = thumbnail + title + current price + an editable
 * price box. Edit the ones that changed, hit "Save N" → one PATCH per
 * changed row (priceCents). Card images aren't re-rendered (same accepted
 * quirk as the price-refresh crons); the feed reads the structured price.
 *
 * Mobile design mirrors WarehouseQuickAddPage: single column, ≥44px touch
 * targets, 16px inputs (no iOS zoom), sticky bottom action.
 */
import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";
import { WAREHOUSE_CHANNELS } from "./warehouseChannels.js";
import { WarehouseEditModal } from "./WarehouseEditModal.jsx";
import { thumbUrl } from "../media.js";

// Shared with the quick-add page; Vite dedups this into the same chunk.
const BarcodeScanner = lazy(() =>
  import("./BarcodeScanner.jsx").then((m) => ({ default: m.BarcodeScanner })));

const usd = (cents) => (cents == null ? "" : (cents / 100).toFixed(2));
const toCents = (s) => {
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
// Short "updated N ago" without pulling a date lib.
function ago(epochSec) {
  if (!epochSec) return "";
  const d = Math.floor((Date.now() / 1000 - epochSec) / 86400);
  if (d <= 0) return "today";
  if (d === 1) return "1d ago";
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export function WarehousePriceWorklist() {
  const t = useT();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);          // warehouse posts
  const [edits, setEdits] = useState({});           // postId -> price string (USD)
  const [store, setStore] = useState("all");        // channel filter
  const [view, setView] = useState("all");          // all | toFinish | published
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);  // camera overlay open?
  const [scanCode, setScanCode] = useState(null);   // active scan filter (null = off)
  const [scanIds, setScanIds] = useState([]);       // post ids matched by the scan
  const [editPost, setEditPost] = useState(null);   // row tapped → full edit modal

  // Pull posts from every warehouse channel in parallel, keep only the
  // hand-added ones (source "warehouse:*") — scraped items in shared
  // channels (e.g. costco apparel) are refreshed by the cron, not here.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const lists = await Promise.all(
        WAREHOUSE_CHANNELS.flatMap((c) => [
          api.getPosts({ channel: c.slug, status: "published", limit: 500 }).catch(() => []),
          // Draft imports waiting to be finished (price/desc added → published).
          api.getPosts({ channel: c.slug, status: "draft", limit: 500 }).catch(() => []),
        ]),
      );
      // Keep hand-added warehouse posts AND Telegram-imported stubs
      // (source "costco-import") — both are edited and published here.
      const all = lists.flat().filter(
        (p) => typeof p.source === "string" &&
          (p.source.startsWith("warehouse:") || p.source === "costco-import"),
      );
      // De-dupe (a post only lives in one channel, but be safe) + sort by
      // channel then title so the list follows a store walk.
      const seen = new Set();
      const uniq = [];
      for (const p of all) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        uniq.push(p);
      }
      // Unfinished (draft) imports float to the top so they get worked off
      // first; then by store, then title for a tidy store walk.
      const rank = (p) => (p.status !== "published" ? 0 : 1);
      uniq.sort((a, b) =>
        rank(a) - rank(b) ||
        (a.channel?.name || "").localeCompare(b.channel?.name || "") ||
        (a.title || "").localeCompare(b.title || ""));
      setPosts(uniq);
    } catch (e) {
      toast.error(e.message || String(e));
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Scan-to-find: look the scanned code up by upc OR item_number, merge any
  // matches into the list (they may live outside the warehouse channels we
  // loaded), and filter the view down to them so the operator lands right
  // on the item to reprice. No match → a "not listed" banner.
  const onScanFind = useCallback(async (text) => {
    setScanning(false);
    const code = (text || "").trim();
    if (!code) return;
    try {
      const matches = await api.getPosts({ code });
      setScanCode(code);
      setScanIds((matches || []).map((m) => m.id));
      if (matches && matches.length) {
        setPosts((prev) => {
          const byId = new Map(prev.map((p) => [p.id, p]));
          for (const m of matches) byId.set(m.id, { ...byId.get(m.id), ...m });
          return [...byId.values()];
        });
        toast.success(t("scanfind.found", { n: matches.length }));
      } else {
        toast.error(t("scanfind.none"));
      }
    } catch (e) {
      toast.error(e.message || String(e));
    }
  }, [toast, t]);

  const clearScan = useCallback(() => { setScanCode(null); setScanIds([]); }, []);

  // Stores that actually have items, for the filter chips.
  const stores = useMemo(() => {
    const counts = {};
    for (const p of posts) {
      const s = p.channel?.slug || "?";
      counts[s] = (counts[s] || 0) + 1;
    }
    return WAREHOUSE_CHANNELS
      .filter((c) => counts[c.slug])
      .map((c) => ({ ...c, count: counts[c.slug] }));
  }, [posts]);

  // Counts for the To-finish / Published view chips.
  const viewCounts = useMemo(() => ({
    all: posts.length,
    toFinish: posts.filter((p) => p.status !== "published").length,
    published: posts.filter((p) => p.status === "published").length,
  }), [posts]);

  const visible = useMemo(() => {
    // Scan mode overrides the store filter: show just the matched posts.
    if (scanCode != null) {
      const ids = new Set(scanIds);
      return posts.filter((p) => ids.has(p.id));
    }
    let list = store === "all" ? posts : posts.filter((p) => p.channel?.slug === store);
    if (view === "toFinish") list = list.filter((p) => p.status !== "published");
    else if (view === "published") list = list.filter((p) => p.status === "published");
    return list;
  }, [posts, store, scanCode, scanIds, view]);

  // A row is dirty when its edited value differs from the stored price.
  const dirtyIds = useMemo(
    () => Object.keys(edits).filter((id) => {
      const p = posts.find((x) => x.id === id);
      if (!p) return false;
      const c = toCents(edits[id]);
      return c != null && c !== p.priceCents;
    }),
    [edits, posts],
  );

  async function saveAll() {
    if (!dirtyIds.length || saving) return;
    setSaving(true);
    let ok = 0;
    const failed = [];
    for (const id of dirtyIds) {
      const cents = toCents(edits[id]);
      try {
        await api.patchPost(id, { priceCents: cents });
        ok += 1;
        // Reflect locally: update price + updatedAt, clear the edit.
        setPosts((prev) => prev.map((p) =>
          p.id === id ? { ...p, priceCents: cents, updatedAt: Math.floor(Date.now() / 1000) } : p));
        setEdits((prev) => { const n = { ...prev }; delete n[id]; return n; });
      } catch (e) {
        failed.push(id);
      }
    }
    setSaving(false);
    if (failed.length) toast.error(t("worklist.savedPartial", { ok, fail: failed.length }));
    else toast.success(t("worklist.saved", { n: ok }));
  }

  const inp = {
    width: "100%", minHeight: 44, padding: "8px 10px", fontSize: 16,
    fontFamily: T_NEW.fontMono, color: T_NEW.ink, background: T_NEW.paper,
    border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r2,
    boxSizing: "border-box", textAlign: "right",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 80 }}>
      {/* Camera scanner overlay (lazy chunk, same as quick-add) */}
      {scanning && (
        <Suspense fallback={
          <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#000",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: T_NEW.meta, fontFamily: T_NEW.fontMono }}>
            {t("scan.starting")}
          </div>
        }>
          <BarcodeScanner onResult={onScanFind} onClose={() => setScanning(false)} />
        </Suspense>
      )}

      {/* Full edit modal — opened by tapping a row's thumb/title */}
      {editPost && (
        <WarehouseEditModal
          post={editPost}
          onClose={() => setEditPost(null)}
          onSaved={({ id, patch, channelChanged, status }) => {
            setEditPost(null);
            if (channelChanged) { load(); return; }  // moved store → re-fetch
            setPosts((prev) => prev.map((pp) => pp.id === id ? {
              ...pp, title: patch.title, brand: patch.brand,
              priceCents: patch.priceCents, originalPriceCents: patch.originalPriceCents,
              itemNumber: patch.itemNumber, upc: patch.upc,
              bodyRu: patch.bodyRu, bodyEs: patch.bodyEs, bodyEn: patch.bodyEn,
              saleValidThrough: patch.saleValidThrough,
              status: status || pp.status, showText: true,
              updatedAt: Math.floor(Date.now() / 1000),
            } : pp));
            setEdits((prev) => { const n = { ...prev }; delete n[id]; return n; });
          }}
        />
      )}

      {/* Scan to find an item to reprice */}
      <Btn variant="outline" onClick={() => setScanning(true)}
           style={{ minHeight: 44, width: "100%" }}>
        📷 {t("scanfind.button")}
      </Btn>

      {/* Scan result banner */}
      {scanCode != null && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          padding: "8px 12px", borderRadius: T_NEW.r2,
          background: scanIds.length ? T_NEW.persimmonSoft : T_NEW.paperSoft,
          border: `1px solid ${T_NEW.ink12}`,
        }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink}>
            {scanIds.length
              ? t("scanfind.resultFound", { code: scanCode, n: scanIds.length })
              : t("scanfind.resultNone", { code: scanCode })}
          </Mono>
          <button onClick={clearScan}
            style={{ flex: "0 0 auto", minHeight: 32, padding: "4px 10px", cursor: "pointer",
                     border: "none", background: "transparent", color: T_NEW.persimmon,
                     fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, fontWeight: 600,
                     textTransform: "uppercase", letterSpacing: T_NEW.monoTrack }}>
            {t("scanfind.clear")}
          </button>
        </div>
      )}

      {/* Finish-state filter — shown only when there are draft imports left.
          Lets the operator focus on "To finish" (drafts) vs "Published". */}
      {scanCode == null && viewCounts.toFinish > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[["all", t("worklist.view.all"), viewCounts.all],
            ["toFinish", t("worklist.view.toFinish"), viewCounts.toFinish],
            ["published", t("worklist.view.published"), viewCounts.published]].map(([v, lbl, n]) => {
            const active = v === view;
            return (
              <button key={v} onClick={() => setView(v)}
                style={{
                  minHeight: 32, padding: "5px 11px", borderRadius: 18,
                  border: `1px solid ${active ? T_NEW.persimmon : T_NEW.ink12}`,
                  background: active ? T_NEW.persimmon : "transparent",
                  color: active ? "#fff" : T_NEW.ink, cursor: "pointer",
                  fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
                }}>
                {lbl} · {n}
              </button>
            );
          })}
        </div>
      )}

      {/* Store filter (hidden while a scan filter is active) */}
      {scanCode == null && stores.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[{ slug: "all", label: t("worklist.all"), count: posts.length }, ...stores].map((c) => {
            const active = c.slug === store;
            return (
              <button key={c.slug} onClick={() => setStore(c.slug)}
                style={{
                  minHeight: 32, padding: "5px 11px", borderRadius: 18,
                  border: `1px solid ${active ? T_NEW.persimmon : T_NEW.ink12}`,
                  background: active ? T_NEW.persimmon : "transparent",
                  color: active ? "#fff" : T_NEW.ink, cursor: "pointer",
                  fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
                }}>
                {c.label} · {c.count}
              </button>
            );
          })}
        </div>
      )}

      {loading && <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta }}>{t("worklist.loading")}</p>}
      {!loading && posts.length === 0 && (
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.body }}>{t("worklist.empty")}</p>
      )}

      {/* Rows */}
      {visible.map((p) => {
        const editing = edits[p.id];
        const dirty = editing != null && toCents(editing) != null && toCents(editing) !== p.priceCents;
        return (
          <div key={p.id}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 0", borderBottom: `1px solid ${T_NEW.ink12}`,
            }}>
            {/* Thumb + title — tap to open the full edit modal. The price
                box (right) keeps its own inline quick-edit, untouched. */}
            <div onClick={() => setEditPost(p)}
                 style={{ width: 48, height: 48, flex: "0 0 auto", borderRadius: T_NEW.r1,
                          overflow: "hidden", background: T_NEW.paperSoft, cursor: "pointer",
                          border: `1px solid ${T_NEW.ink12}` }}>
              {p.images?.[0]?.url && (
                <img src={thumbUrl(p.images[0].url, 240)} alt="" loading="lazy"
                     style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </div>
            <div onClick={() => setEditPost(p)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
              <div style={{ fontSize: T_NEW.body, color: T_NEW.ink, lineHeight: 1.25,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.title}
              </div>
              <Mono color={T_NEW.ink38} size={T_NEW.micro}>
                {p.status !== "published" && (
                  <span style={{ color: T_NEW.persimmon, fontWeight: 700 }}>{t("worklist.draft")} · </span>
                )}
                {p.channel?.name} · {p.publicCode || "—"} · {ago(p.updatedAt)} · ✎
              </Mono>
            </div>
            {/* Price box */}
            <div style={{ flex: "0 0 92px", position: "relative" }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
                             color: T_NEW.ink38, fontSize: 15, pointerEvents: "none" }}>$</span>
              <input type="number" inputMode="decimal" step="0.01" min="0"
                     value={editing != null ? editing : usd(p.priceCents)}
                     onChange={(e) => setEdits((prev) => ({ ...prev, [p.id]: e.target.value }))}
                     style={{ ...inp, paddingLeft: 18,
                              borderColor: dirty ? T_NEW.persimmon : T_NEW.ink12,
                              background: dirty ? T_NEW.persimmonSoft : T_NEW.paper }} />
            </div>
          </div>
        );
      })}

      {/* Sticky save */}
      <div style={{
        position: "sticky", bottom: 0, background: T_NEW.paper, marginTop: 8,
        padding: "12px 0 8px", borderTop: `1px solid ${T_NEW.ink12}`,
      }}>
        <Btn variant="accent" disabled={!dirtyIds.length || saving}
             onClick={saveAll}
             style={{ minHeight: 48, width: "100%", fontSize: 16, fontWeight: 600 }}>
          {saving ? t("worklist.saving")
                  : dirtyIds.length ? t("worklist.saveN", { n: dirtyIds.length })
                                    : t("worklist.noChanges")}
        </Btn>
      </div>
    </div>
  );
}
